import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import {
  BorderRadius,
  LightModeSemanticColors,
} from '@enechain/ecloud-designsystem'

import { ChartTooltip } from '~/components/common/ChartTooltip'
import type { AreaDef, KomaRow } from '~/pages/SpotPrice/constants'
import { AREAS } from '~/pages/SpotPrice/constants'
import { formatTick, xTicks } from '~/pages/SpotPrice/models/chartTicks'
import { chartYDomain } from '~/pages/SpotPrice/models/komaRows'

type TooltipProps = React.ComponentProps<typeof ChartTooltip> & {
  isMultiDay: boolean
}

/**
 * 共通 tooltip の見出しを、 X 軸キー (`YYYY-MM-DD HH:MM`) から共通側が読める形へ直す
 * (design `_smTooltipContent`)。 単日は `HH:MM` (共通側が「コマ N / HH:MM – HH:MM」へ整形)、
 * 複数日は日付を足して「どの日か」を示す。 値は 円/kWh の小数なので 2 桁で出す。
 */
const SpotPriceTooltip = ({
  isMultiDay,
  label,
  ...rest
}: TooltipProps): React.ReactElement => (
  <ChartTooltip
    {...rest}
    formatValue={(v) => v.toFixed(2)}
    label={
      typeof label === 'string' && label.length >= 16
        ? isMultiDay
          ? `${label.slice(5, 10).replace('-', '/')} ${label.slice(11)}`
          : label.slice(11)
        : label
    }
  />
)

type Props = {
  komaRows: KomaRow[]
  visibleAreas: Set<string>
  /** 複数日の連結表示か (目盛りと tooltip の見出しが変わる)。 */
  isMultiDay: boolean
}

export const PriceChart = ({
  komaRows,
  visibleAreas,
  isMultiDay,
}: Props): React.ReactElement => {
  const gridColor = LightModeSemanticColors.Border.lowEmphasis.value
  const tickColor = LightModeSemanticColors.Text.midEmphasis.value
  const tickFontSize = 12

  const visibleAreaDefs = AREAS.filter((a: AreaDef) => visibleAreas.has(a.code))

  return (
    <div
      style={{
        height: 420,
        overflow: 'hidden',
        background: LightModeSemanticColors.Surface.secondary.value,
        // 8080 `.sm-chart-wrap { border-radius: var(--radius-md) }` (6px)
        borderRadius: BorderRadius.radiiMd.value,
      }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          margin={{ top: 12, right: 16, left: 0, bottom: 4 }}
          data={komaRows}
        >
          <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
          <XAxis
            dataKey="x"
            tick={{ fontSize: tickFontSize, fill: tickColor }}
            tickFormatter={(x: string) => formatTick(x, isMultiDay)}
            ticks={xTicks(komaRows, isMultiDay)}
          />
          <YAxis
            width={44}
            domain={chartYDomain(komaRows)}
            label={{
              value: '円/kWh',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fontSize: tickFontSize, fill: tickColor },
            }}
            tick={{ fontSize: tickFontSize, fill: tickColor }}
          />
          {/* 共通 tooltip (design `js/charts.js` の `darkTooltipContent`) を使う。
              8080 のこの画面も同じ helper を `_smTooltipContent` 経由で呼んでおり
              (spot-price.html:551-568)、 48点データのグラフと同じ見た目になる
              (2026-08-31 デザイナー指摘「グラフの tooltip UI は 8080 準拠」)。
              X 軸キーは `YYYY-MM-DD HH:MM` なので、 `SpotPriceTooltip` が見出しを
              共通側の読める `HH:MM` (複数日は `MM/DD HH:MM`) に直し、 単日は共通側が
              「コマ N / HH:MM – HH:MM」へ整形する。
              値は 円/kWh の小数なので既定の整数丸めでは 8.66 → 9 になる。 8080 は
              payload を `toFixed(2)` した文字列にして丸めを回避しているので、
              こちらは同じ意図を `formatValue` で表す。 */}
          <Tooltip
            content={<SpotPriceTooltip isMultiDay={isMultiDay} />}
            offset={16}
          />
          {visibleAreaDefs.map((a: AreaDef) => (
            <Line
              key={a.code}
              stroke={a.color}
              activeDot={{ r: 3 }}
              dataKey={a.code}
              dot={false}
              isAnimationActive={false}
              name={a.name}
              strokeDasharray={a.isSystem === true ? '5 4' : undefined}
              strokeWidth={2}
              // spec の「折れ線チャート」。8080 も `type:'linear'` (点と点を直線で結ぶ)
              type="linear"
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
